import React, { useState, useEffect } from 'react';
import ScrollToTop from './ScrollToTop';
import { useNavigate } from 'react-router-dom';
import { useQuote } from './QuoteContext';
import { useAuth } from './AuthContext';
import { motion } from 'framer-motion';

const CompanyInfo = () => {
  const navigate = useNavigate();
  const { updateQuoteData, quoteData } = useQuote();
  const { profile } = useAuth();

  function generateDevisNo() {
    const d = new Date();
    const ds = `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getFullYear()).slice(-2)}`;
    return `P${ds}${Math.floor(100 + Math.random() * 900)}`;
  }

  const [formData, setFormData] = useState({
    devisNo: generateDevisNo(),
    date: new Date().toISOString().split('T')[0],
    contact: '',
    email: 'contact@takoussane.com',
    tel: '(+221) 33 836 67 83',
    mob: '(+221) 78 346 88 51',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (quoteData.companyInfo?.contact) {
      setFormData(quoteData.companyInfo);
    } else if (profile?.nom) {
      // Pré-remplir avec le prénom uniquement pour la proximité client
      const prenom = profile.nom.split(' ')[0];
      // Pré-remplir le mobile pro de la commerciale si disponible
      const mob = profile.mobile || '(+221) 78 346 88 51';
      // Pré-remplir l'email de la commerciale
      const email = profile.email || 'contact@takoussane.com';
      setFormData(prev => ({ ...prev, contact: prenom, mob, email }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.contact?.trim()) newErrors.contact = 'Champ requis';
    if (!formData.email?.trim()) newErrors.email = 'Champ requis';
    if (!formData.tel?.trim()) newErrors.tel = 'Champ requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      updateQuoteData({
        companyInfo: formData,
        quoteNumber: formData.devisNo,
        date: formData.date,
      });
      navigate('/quote-form');
    }
  };

  const handleSave = () => {
    updateQuoteData({ companyInfo: formData });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleReset = () => {
    const prenom = profile?.nom ? profile.nom.split(' ')[0] : '';
    const mob = profile?.mobile || '(+221) 78 346 88 51';
    const email = profile?.email || 'contact@takoussane.com';
    setFormData({
      devisNo: generateDevisNo(),
      date: new Date().toISOString().split('T')[0],
      contact: prenom,
      email,
      tel: '(+221) 33 836 67 83',
      mob,
    });
    setErrors({});
  };

  const inputClass = (field) =>
    `w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300 ${errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

  return (
    <>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
      <div className="container mx-auto px-4">
        <form onSubmit={handleSubmit} className="space-y-4">

          <div>
            <label className="block font-semibold mb-1">Numéro de devis :</label>
            <input type="text" value={formData.devisNo} readOnly
              className="w-full border border-gray-200 rounded p-2 bg-gray-100 text-gray-500" />
          </div>

          <div>
            <label className="block font-semibold mb-1">Date :</label>
            <input type="date" name="date" value={formData.date} readOnly disabled title="Date d'émission automatique (non modifiable)"
              className="w-full border border-gray-300 rounded p-2 bg-gray-100 text-gray-600 cursor-not-allowed focus:outline-none" />
          </div>

          <div>
            <label className="block font-semibold mb-1">
              Nom du contact : <span className="text-red-500">*</span>
              <span className="text-gray-400 text-xs font-normal ml-2">(prénom affiché sur le devis)</span>
            </label>
            <input type="text" name="contact" value={formData.contact} onChange={handleChange}
              placeholder="Prénom" className={inputClass('contact')} />
            {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
          </div>

          <div>
            <label className="block font-semibold mb-1">Email : <span className="text-red-500">*</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              placeholder="email@exemple.com" className={inputClass('email')} />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block font-semibold mb-1">Téléphone fixe : <span className="text-red-500">*</span></label>
            <input type="tel" name="tel" value={formData.tel} onChange={handleChange}
              placeholder="33 XXX XX XX" className={inputClass('tel')} />
            {errors.tel && <p className="text-red-500 text-xs mt-1">{errors.tel}</p>}
          </div>

          <div>
            <label className="block font-semibold mb-1">
              Téléphone mobile :
              <span className="text-gray-400 text-xs font-normal ml-2">(laisser vide si non souhaité)</span>
            </label>
            <input type="tel" name="mob" value={formData.mob} onChange={handleChange}
              placeholder="77 XXX XX XX" className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded p-3 text-red-700 text-sm">
              ⚠️ Veuillez remplir tous les champs obligatoires.
            </div>
          )}

          {saveSuccess && (
            <div className="bg-green-50 border border-green-300 rounded p-3 text-green-700 text-sm">
              ✓ Données sauvegardées avec succès !
            </div>
          )}

          <div className="mt-6 flex justify-between items-center">
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={() => navigate('/client-info')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium">
                Précédent
              </button>
              <button type="button" onClick={handleSave}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50 text-sm font-medium">
                Sauvegarder
              </button>
              <button type="button" onClick={handleReset}
                className="px-4 py-2 border border-orange-500 text-orange-500 rounded hover:bg-orange-50 text-sm font-medium">
                Réinitialiser
              </button>
              <button type="button" onClick={() => setShowPreview(true)}
                className="px-4 py-2 border border-purple-500 text-purple-500 rounded hover:bg-purple-50 text-sm font-medium">
                Prévisualiser
              </button>
            </div>
            <button type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold">
              Suivant
            </button>
          </div>
        </form>
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Aperçu</h3>
            <div className="space-y-2 text-sm">
              <p><strong>N° devis :</strong> {formData.devisNo}</p>
              <p><strong>Date :</strong> {formData.date}</p>
              <p><strong>Contact :</strong> {formData.contact}</p>
              <p><strong>Email :</strong> {formData.email}</p>
              <p><strong>Tél fixe :</strong> {formData.tel}</p>
              {formData.mob && <p><strong>Mobile :</strong> {formData.mob}</p>}
            </div>
            <button onClick={() => setShowPreview(false)}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm">
              Fermer
            </button>
          </div>
        </div>
      )}
    </motion.div>
    <ScrollToTop />
    </>
  );
};

export default CompanyInfo;
